import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

export interface EmptyStateProps {
  /** What is not here, stated plainly. */
  title: string;
  /** What to do about it. An empty screen is an invitation to act. */
  description?: string;
  /** A line icon, sat in a soft round well above the title. */
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/**
 * Nothing here yet.
 *
 * Quiet by construction: a muted icon in a soft well, one short line, and at
 * most one thing to do. An empty table is not a failure worth shouting about.
 *
 * It holds a minimum height so an empty panel is an area rather than a strip.
 * A three-line notice floating at the top of a thousand-pixel screen reads as
 * a page that stopped loading; the same words in a panel with room around them
 * read as an answer.
 */
export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex min-h-56 flex-col items-center justify-center px-6 py-12 text-center",
        className,
      )}
    >
      {icon ? (
        <div
          className="mb-4 grid size-12 place-items-center rounded-full bg-ink-100 text-fg-subtle"
          aria-hidden="true"
        >
          {icon}
        </div>
      ) : null}
      <p className="text-md font-semibold text-fg">{title}</p>
      {description ? (
        <p className="mx-auto mt-1.5 max-w-sm text-base text-fg-subtle">{description}</p>
      ) : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}
